import * as React from "react";
import { useBreakpointValue } from "@chakra-ui/react";
import { Textarea, IconButton, Box, Flex, Spinner } from "@chakra-ui/react";
import { TableSelectPopover } from "@/components/ui/table-select-popover";
import { TableSelectDrawer } from "@/components/ui/table-select-drawer";
import { ToolSelectPopover } from "@/components/ui/tool-select-popover";
import { ToolSelectDrawer } from "@/components/ui/tool-select-drawer";
import { UploadedFileDisplay } from "@/components/ui/uploaded-file-display";
import { useQuery } from "@tanstack/react-query";
import {
  TablesService,
  StorageService,
  ToolsService,
  ChatService,
  type ToolPublic,
} from "@/client";
import { FiSend, FiPlusSquare } from "react-icons/fi";
import useCustomToast from "@/hooks/useCustomToast";

interface ChatInputProps {
  isStreaming: boolean;
  onSend: (msg: string, options?: Record<string, unknown>) => void;
}

export const ChatInput = ({ isStreaming, onSend }: ChatInputProps) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [message, setMessage] = React.useState("");
  const [selectedTables, setSelectedTables] = React.useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = React.useState<string[]>([]);
  const [selectedTools, setSelectedTools] = React.useState<string[]>([]);
  // const [rows, setRows] = React.useState(2);
  // const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  interface UploadedFile {
    fileId: string;
    fileName: string;
    contentType: string;
    isImage: boolean;
  }

  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed) return;

    // 여러 파일이 있으면 첫 번째 파일의 ID만 전송 (또는 배열로 전송 가능)
    const fileId = uploadedFiles.length > 0 ? uploadedFiles[0].fileId : null;

    onSend(trimmed, {
      tables: selectedTables,
      folders: selectedFolders,
      tools: selectedTools,
      file_id: fileId, // 업로드된 파일 ID 포함
    });

    setMessage("");
    setUploadedFiles([]);
  };

  const { data: tables } = useQuery({
    queryKey: ["tables"],
    queryFn: () => TablesService.readTables({ skip: 0, limit: 100 }),
  });

  const { data: folders } = useQuery({
    queryKey: ["folders"],
    queryFn: () => StorageService.readFolders({ skip: 0, limit: 100 }),
  });

  const tableOptions = React.useMemo(
    () =>
      tables?.data?.map((t) => ({
        label: t.table_name,
        value: t.id,
      })) ?? [],
    [tables?.data]
  );

  const folderOptions = React.useMemo(
    () =>
      folders?.data?.map((t) => ({
        label: t.folder_name,
        value: t.id,
      })) ?? [],
    [folders?.data]
  );

  //툴 선택
  const { data: tools } = useQuery<ToolPublic[]>({
    queryKey: ["tools"],
    queryFn: () => ToolsService.getAllTools().then((res) => res.data),
  });

  const toolOptions = React.useMemo(
    () =>
      tools?.map((t) => ({
        label: t.name,
        value: t.id,
        icon: t.icon,
      })) ?? [],
    [tools]
  );

  const hasInitialized = React.useRef(false);

  React.useEffect(() => {
    if (!hasInitialized.current) {
      // 초기 로드 시 모든 항목 선택
      if (tableOptions.length > 0) {
        setSelectedTables(tableOptions.map((t) => t.value));
      }
      if (folderOptions.length > 0) {
        setSelectedFolders(folderOptions.map((f) => f.value));
      }
      hasInitialized.current = true;
    }
  }, [tableOptions, folderOptions]);

  // props 변경 시 선택 상태 유지하면서 새로운 항목만 추가
  React.useEffect(() => {
    if (hasInitialized.current) {
      setSelectedTables((prev) => {
        const newTables = tableOptions.map((t) => t.value);
        const existingTables = prev.filter((t) => newTables.includes(t));
        return existingTables.length > 0 ? existingTables : newTables;
      });

      setSelectedFolders((prev) => {
        const newFolders = folderOptions.map((f) => f.value);
        const existingFolders = prev.filter((f) => newFolders.includes(f));
        return existingFolders.length > 0 ? existingFolders : newFolders;
      });
    }
  }, [tableOptions, folderOptions]);

  const handleTableFolderSelect = React.useCallback(
    (tables: string[], folders: string[]) => {
      setSelectedTables(tables);
      setSelectedFolders(folders);
      console.log("✅ Tables:", tables);
      console.log("✅ Folders:", folders);
    },
    []
  );

  const handleToolSelect = React.useCallback((selected: string[]) => {
    setSelectedTools(selected);
    console.log("✅ Tools:", selected);
  }, []);

  const handleFileUpload = (
    fileId: string,
    fileName: string,
    contentType: string,
    isImage: boolean
  ) => {
    setUploadedFiles((prev) => [
      ...prev,
      { fileId, fileName, contentType, isImage },
    ]);
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.fileId !== fileId));
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // 여러 파일 처리
    const fileArray = Array.from(files);
    setIsUploading(true);

    for (const file of fileArray) {
      // 파일 크기 검증 (50MB 제한)
      if (file.size > 50 * 1024 * 1024) {
        showErrorToast(`${file.name}: 파일 크기가 50MB를 초과합니다.`);
        continue;
      }

      // 지원하는 파일 형식 검증
      const supportedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
        "text/markdown",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      if (!supportedTypes.includes(file.type)) {
        showErrorToast(`${file.name}: 지원하지 않는 파일 형식입니다.`);
        continue;
      }

      try {
        const response = await ChatService.uploadDocument({
          formData: { file },
        });

        if (response?.file_id) {
          const fileId = response.file_id as string;
          const fileName = (response.filename as string) || file.name;
          const contentType = (response.content_type as string) || file.type;
          const isImage =
            (response.is_image as boolean) || file.type.startsWith("image/");
          handleFileUpload(fileId, fileName, contentType, isImage);
        }
      } catch (error) {
        console.error("파일 업로드 실패:", error);
        showErrorToast(`${file.name} 업로드 중 오류가 발생했습니다.`);
      }
    }

    setIsUploading(false);
    // 파일 입력 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (fileArray.length > 0) {
      showSuccessToast(`${fileArray.length}개 파일이 업로드되었습니다.`);
    }
  };

  return (
    <Box w="100%">
      {/* Toggle buttons row */}
      <Flex mb={2} gap={2} justify="space-between">
        {isMobile ? (
          <TableSelectDrawer
            tables={tableOptions}
            folders={folderOptions}
            onSelect={handleTableFolderSelect}
          />
        ) : (
          <TableSelectPopover
            tables={tableOptions}
            folders={folderOptions}
            onSelect={handleTableFolderSelect}
          />
        )}
        {/* <Button width="100px" variant="outline" colorScheme="blue">
          Option 2
        </Button>
        <Button width="100px" variant="outline" colorScheme="blue">
          Option 3
        </Button> */}
        {isMobile ? (
          <ToolSelectDrawer tools={toolOptions} onSelect={handleToolSelect} />
        ) : (
          <ToolSelectPopover tools={toolOptions} onSelect={handleToolSelect} />
        )}
      </Flex>

      {/* Textarea and Send Button */}
      <Box position="relative">
        <Textarea
          value={message}
          onChange={handleInput}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={
            isStreaming ? "AI 답변 기다리는 중..." : "질문을 입력해주세요."
          }
          resize="none"
          overflowY="auto"
          minHeight="64px"
          maxHeight="180px"
          focusRingColor="blue.500"
          pr="40px"
          pl="40px"
          px={4}
          py={3}
          borderRadius="md"
          autoresize
        />
        <Box position="absolute" bottom="8px" right="8px">
          <IconButton
            aria-label="보내기"
            variant="ghost"
            resize="none"
            onClick={handleSend}
            disabled={!message.trim()}
            _hover={{
              bg: "gray.100", // Background color on hover
              borderRadius: "full", // Fully rounded (circle)
            }}
          >
            {isStreaming ? <Spinner size="sm" /> : <FiSend />}
          </IconButton>
        </Box>
        <Box position="absolute" bottom="8px" left="8px">
          <IconButton
            aria-label="파일 첨부"
            variant="ghost"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || isStreaming}
            _hover={{
              bg: "gray.100",
              borderRadius: "full",
            }}
          >
            {isUploading ? <Spinner size="sm" /> : <FiPlusSquare />}
          </IconButton>
        </Box>
        {/* 숨겨진 파일 입력 */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,.md,.jpg,.jpeg,.png,.gif,.webp"
          onChange={handleFileInputChange}
          multiple
          style={{ display: "none" }}
        />
      </Box>

      {/* 업로드된 파일 썸네일 표시 */}
      {uploadedFiles.length > 0 && (
        <Flex mt={2} gap={2} flexWrap="wrap">
          {uploadedFiles.map((file) => (
            <UploadedFileDisplay
              key={file.fileId}
              fileId={file.fileId}
              fileName={file.fileName}
              contentType={file.contentType}
              isImage={file.isImage}
              onRemove={() => handleRemoveFile(file.fileId)}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
};
